import { useEffect, useState } from "react";
import { Loader2, Search, Plus, Check } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";

const STROKE = 1.5;

// Feature 3 — "Add candidate to role" dialog used from the role pipeline view.
// Lists workspace candidates with search + an inline "create new candidate"
// shortcut that creates + assigns in one flow. Recruiter+ only (caller gates).
export function AddCandidateToRoleDialog({ open, onOpenChange, roleId, existingIds = [], onAdded }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/candidates/search`, { params: { q } })
      .then(({ data }) => setResults(data.candidates || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [q, open]);

  const assign = async (candidateId) => {
    setBusyId(candidateId);
    try {
      const { data } = await api.post(`/roles/${roleId}/candidates`, { candidate_id: candidateId });
      toast.success(data.created ? "Candidate added to role" : "Candidate already in this role");
      onAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add candidate");
    } finally {
      setBusyId(null);
    }
  };

  const createAndAssign = async () => {
    const name = q.trim();
    if (!name) return;
    setCreating(true);
    try {
      const { data: cand } = await api.post(`/candidates`, { name });
      await api.post(`/roles/${roleId}/candidates`, { candidate_id: cand.candidate_id });
      toast.success(`Created ${name} and added to role`);
      onAdded?.();
      setQ("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create candidate");
    } finally {
      setCreating(false);
    }
  };

  const exactMatch = results.some((c) => (c.name || "").toLowerCase() === q.trim().toLowerCase());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-candidate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-medium">Add candidate to role</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-600">
            Search existing candidates or create a new one. Adding is idempotent.
          </DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" strokeWidth={STROKE} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search or type a new candidate name…"
            className="w-full rounded-md border border-ink-200 py-2 pl-9 pr-3 text-[14px] focus:border-emerald-700 focus:outline-none"
            data-testid="add-candidate-search"
          />
        </div>
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {loading && <div className="flex justify-center p-4"><Loader2 className="h-4 w-4 animate-spin text-ink-400" strokeWidth={STROKE} /></div>}
          {!loading && results.map((c) => {
            const assigned = existingIds.includes(c.candidate_id);
            return (
              <div key={c.candidate_id} className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-ink-50" data-testid={`add-candidate-row-${c.candidate_id}`}>
                <div>
                  <div className="text-[14px] font-medium text-ink-900">{c.name}</div>
                  {c.email && <div className="text-[12px] text-ink-500">{c.email}</div>}
                </div>
                {assigned ? (
                  <span className="inline-flex items-center gap-1 text-[12px] text-emerald-800" data-testid={`add-candidate-assigned-${c.candidate_id}`}>
                    <Check className="h-3.5 w-3.5" strokeWidth={STROKE} /> In pipeline
                  </span>
                ) : (
                  <button
                    onClick={() => assign(c.candidate_id)}
                    disabled={busyId === c.candidate_id}
                    className="inline-flex items-center gap-1 rounded-md bg-emerald-800 px-2.5 py-1 text-[12px] font-medium text-white hover:bg-emerald-900 disabled:opacity-50"
                    data-testid={`add-candidate-assign-${c.candidate_id}`}
                  >
                    {busyId === c.candidate_id ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={STROKE} /> : <Plus className="h-3 w-3" strokeWidth={STROKE} />}
                    Add
                  </button>
                )}
              </div>
            );
          })}
          {!loading && q.trim() && !exactMatch && (
            <button
              onClick={createAndAssign}
              disabled={creating}
              className="flex w-full items-center gap-2 rounded-md border border-dashed border-emerald-700/40 px-3 py-2 text-left text-[13px] text-emerald-900 hover:bg-emerald-50 disabled:opacity-50"
              data-testid="add-candidate-create-new"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} /> : <Plus className="h-4 w-4" strokeWidth={STROKE} />}
              Create &amp; add &ldquo;{q.trim()}&rdquo;
            </button>
          )}
          {!loading && !results.length && !q.trim() && (
            <div className="p-4 text-center text-[13px] text-ink-400">No candidates yet — type a name to create one.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Feature 3 — "Add to role" dialog used from the candidate profile. Lists the
// workspace's OPEN roles; roles the candidate is already in are shown disabled.
export function AddToRoleDialog({ open, onOpenChange, candidateId, onAdded }) {
  const [roles, setRoles] = useState([]);
  const [assignedIds, setAssignedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get(`/roles`),
      api.get(`/candidates/${candidateId}/roles`),
    ])
      .then(([rolesRes, mineRes]) => {
        setRoles(rolesRes.data || []);
        setAssignedIds((mineRes.data.roles || []).map((x) => x.role.role_id));
      })
      .catch(() => { setRoles([]); setAssignedIds([]); })
      .finally(() => setLoading(false));
  }, [open, candidateId]);

  const assign = async (roleId) => {
    setBusyId(roleId);
    try {
      const { data } = await api.post(`/roles/${roleId}/candidates`, { candidate_id: candidateId });
      toast.success(data.created ? "Added to role" : "Already in this role");
      setAssignedIds((prev) => (prev.includes(roleId) ? prev : [...prev, roleId]));
      onAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add to role");
    } finally {
      setBusyId(null);
    }
  };

  const openRoles = roles.filter((r) => r.status !== "closed");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-to-role-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-medium">Add to role</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-600">
            Put this candidate into a role&rsquo;s pipeline. Roles they&rsquo;re already in are marked.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {loading && <div className="flex justify-center p-4"><Loader2 className="h-4 w-4 animate-spin text-ink-400" strokeWidth={STROKE} /></div>}
          {!loading && openRoles.map((r) => {
            const assigned = assignedIds.includes(r.role_id);
            return (
              <div key={r.role_id} className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-ink-50" data-testid={`add-to-role-row-${r.role_id}`}>
                <div>
                  <div className="text-[14px] font-medium text-ink-900">{r.title}</div>
                  {r.department && <div className="text-[12px] text-ink-500">{r.department}</div>}
                </div>
                {assigned ? (
                  <span className="inline-flex items-center gap-1 text-[12px] text-emerald-800" data-testid={`add-to-role-assigned-${r.role_id}`}>
                    <Check className="h-3.5 w-3.5" strokeWidth={STROKE} /> Assigned
                  </span>
                ) : (
                  <button
                    onClick={() => assign(r.role_id)}
                    disabled={busyId === r.role_id}
                    className="inline-flex items-center gap-1 rounded-md bg-emerald-800 px-2.5 py-1 text-[12px] font-medium text-white hover:bg-emerald-900 disabled:opacity-50"
                    data-testid={`add-to-role-assign-${r.role_id}`}
                  >
                    {busyId === r.role_id ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={STROKE} /> : <Plus className="h-3 w-3" strokeWidth={STROKE} />}
                    Add
                  </button>
                )}
              </div>
            );
          })}
          {!loading && !openRoles.length && (
            <div className="p-4 text-center text-[13px] text-ink-400">No open roles in this workspace yet.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
